import { ColumnBlockModel, ColumnsBlockModel } from '@blocksuite/affine-model';
import {
  findColumnsRow,
  getContainerAncestor,
  matchModels,
} from '@blocksuite/affine-shared/utils';
import type { BlockModel, Store } from '@blocksuite/store';

export type ColumnSide = 'left' | 'right';

/**
 * Makes room for a new column beside `target` and returns its id, so the
 * caller can drop content into it.
 *
 * Two cases:
 * - `target` already sits inside a columns row — insert a sibling column next
 *   to the column holding it, and give it the average width of the existing
 *   columns so the row does not visibly jump.
 * - `target` is a plain note child — wrap it in a fresh row of two equal
 *   columns and return the empty one.
 *
 * Returns null when a column cannot legally be placed here.
 */
export function createAdjacentColumn(
  store: Store,
  target: BlockModel,
  side: ColumnSide
): string | null {
  if (store.readonly) return null;

  // Never wrap the column machinery in itself.
  if (matchModels(target, [ColumnsBlockModel, ColumnBlockModel])) return null;

  const existingColumn = getContainerAncestor(target);
  if (existingColumn) {
    const row = findColumnsRow(existingColumn);
    if (!row) return null;

    const index = row.children.indexOf(existingColumn);
    if (index === -1) return null;

    const columns = row.children.filter(child =>
      matchModels(child, [ColumnBlockModel])
    );
    const averageWidth =
      columns.reduce((sum, column) => sum + column.props.width, 0) /
      (columns.length || 1);

    return store.addBlock(
      'affine:column',
      { width: averageWidth },
      row.id,
      side === 'left' ? index : index + 1
    );
  }

  const parent = target.parent;
  if (!parent) return null;
  if (!store.schema.safeValidate('affine:columns', parent.flavour)) return null;
  if (!store.schema.safeValidate(target.flavour, 'affine:column')) return null;

  const rowId = store.addBlock(
    'affine:columns',
    {},
    parent.id,
    parent.children.indexOf(target)
  );

  const existingId = store.addBlock('affine:column', { width: 1 }, rowId);
  const newId = store.addBlock(
    'affine:column',
    { width: 1 },
    rowId,
    side === 'left' ? 0 : undefined
  );

  const existing = store.getModelById(existingId);
  if (!existing) return null;
  store.moveBlocks([target], existing);

  return newId;
}
